import React from 'react';

/**
 * 🫙⚡ TillJar — the cute animated till (token economy, TOKEN_ECONOMY_SPEC
 * §16 tail / Nina's 08-13 "cute animated till on /explore" go).
 *
 * DATA-AGNOSTIC ON PURPOSE. It renders a level, it does not know what a till
 * is. Today it skins the Claude House FICTION till (🪙, house coins) on the
 * /explore pill; at economy Phase 2 the same vessel renders every space's
 * REAL till (⚡ sparks). The glyph IS the currency firewall — never render
 * fiction and real tills with the same glyph (spec §2 one-way glass).
 *
 * Perf contract (/explore is the perf-critical rail):
 * - Pure CSS animation, zero JS ticking, zero network. Keyframes injected
 *   once per document.
 * - `animate` should be true ONLY for the center card (LiveRailCard idiom);
 *   everything else gets the static frame.
 * - prefers-reduced-motion kills all motion via the injected stylesheet.
 *
 * Levels (derive server-side for real tills — days-of-life, never raw $):
 *   0 = dark ember (broke; still warm, reads "needs love", never "dead")
 *   1 = low   2 = warm   3 = glowing
 */

const STYLE_ID = 'till-jar-css';

function ensureStyles() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
@keyframes tillJarFloat {
  0%   { transform: translateY(3px) scale(0.6); opacity: 0; }
  25%  { opacity: 0.9; }
  100% { transform: translateY(-9px) scale(1); opacity: 0; }
}
@keyframes tillJarGlowPulse {
  0%, 100% { opacity: 0.55; }
  50%      { opacity: 1; }
}
@keyframes tillJarEmber {
  0%, 100% { opacity: 0.25; }
  50%      { opacity: 0.5; }
}
.till-jar-particle { animation: tillJarFloat 2.4s ease-out infinite; }
.till-jar-glow     { animation: tillJarGlowPulse 3s ease-in-out infinite; }
.till-jar-ember    { animation: tillJarEmber 2.8s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) {
  .till-jar-particle { animation: none; opacity: 0; }
  .till-jar-glow, .till-jar-ember { animation: none; }
}`;
  document.head.appendChild(el);
}

// One palette per glyph — the skin half of the currency firewall.
const SKINS = {
  '🪙': { fillA: '#f0c96a', fillB: '#c99b3f', glow: 'rgba(240,201,106,', particle: '#ffe9ad' },
  '⚡': { fillA: '#9ad8ff', fillB: '#5aa7e8', glow: 'rgba(140,200,255,', particle: '#d8f0ff' },
};

// 🫙 Sprite skin (Nina approved 08-14 off the TillJar contact sheet, 60px).
// Five alpha-keyed states minted via the shelf-icon pipeline; the fill
// fraction picks the frame. Thresholds sit between the sheet's minted
// fractions (0 · 3/15 · 8/15 · 13/15 · 15/15).
const SPRITE_BASE = '/till-jar';
const spriteFor = (f) =>
  f >= 1 ? 'brimming' : f >= 0.7 ? 'high' : f >= 0.35 ? 'half' : f > 0 ? 'low' : 'empty';

const GLOW_ALPHA = [0.0, 0.18, 0.38, 0.62]; // per level
const PARTICLES = [0, 1, 2, 3];             // floating motes per level

export default function TillJar({
  level = 0,          // 0..3 discrete state
  fill = null,        // 0..1 fine fill; defaults to level/3
  glyph = '🪙',       // currency skin — 🪙 house fiction, ⚡ real sparks
  animate = false,    // center-card only
  size = 22,
  sprite = false,     // render the minted 5-state PNG instead of the SVG
}) {
  ensureStyles();
  const lv = Math.max(0, Math.min(3, Math.round(level)));
  const frac = Math.max(0, Math.min(1, fill == null ? lv / 3 : fill));
  const skin = SKINS[glyph] || SKINS['🪙'];
  const spriteLv = frac >= 0.75 ? 3 : frac >= 0.35 ? 2 : frac > 0 ? 1 : 0;
  const motes = animate ? PARTICLES[sprite ? spriteLv : lv] : 0;

  if (sprite) {
    return (
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block', width: size, height: size,
          position: 'relative', verticalAlign: 'middle', lineHeight: 0,
        }}
      >
        <img
          src={`${SPRITE_BASE}/till-jar-${spriteFor(frac)}.png`}
          alt=""
          width={size}
          height={size}
          draggable={false}
          className={frac > 0 ? 'till-jar-glow' : undefined}
          style={{
            display: 'block', width: '100%', height: '100%',
            filter: spriteLv > 0
              ? `drop-shadow(0 0 ${2 + spriteLv * 2}px ${skin.glow}${GLOW_ALPHA[spriteLv]}))`
              : 'none',
          }}
        />
        {Array.from({ length: motes }).map((_, i) => (
          <span
            key={i}
            className="till-jar-particle"
            style={{
              position: 'absolute',
              left: `${30 + i * 20}%`,
              bottom: '28%',
              width: 3, height: 3, borderRadius: '50%',
              background: skin.particle,
              animationDelay: `${i * 0.8}s`,
              pointerEvents: 'none',
            }}
          />
        ))}
      </span>
    );
  }

  // Jar interior: x 5..19, y 8..25 (viewBox 24x28). Fill rises from y=25.
  const fillH = 17 * frac;
  const fillY = 25 - fillH;

  return (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-block', width: size, height: Math.round(size * 28 / 24),
        position: 'relative', verticalAlign: 'middle', lineHeight: 0,
      }}
    >
      <svg
        viewBox="0 0 24 28"
        width="100%"
        height="100%"
        className={lv > 0 ? 'till-jar-glow' : undefined}
        style={{
          display: 'block',
          filter: lv > 0 ? `drop-shadow(0 0 ${2 + lv * 2}px ${skin.glow}${GLOW_ALPHA[lv]}))` : 'none',
        }}
      >
        <defs>
          <linearGradient id={`tillFill-${glyph === '⚡' ? 'spark' : 'coin'}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={skin.fillA} />
            <stop offset="100%" stopColor={skin.fillB} />
          </linearGradient>
          <clipPath id="tillJarClip">
            <path d="M6 8 h12 v13 a4 4 0 0 1 -4 4 h-4 a4 4 0 0 1 -4 -4 z" />
          </clipPath>
        </defs>
        {/* glass body */}
        <path
          d="M6 8 h12 v13 a4 4 0 0 1 -4 4 h-4 a4 4 0 0 1 -4 -4 z"
          fill="rgba(255,255,255,0.08)"
          stroke="rgba(255,255,255,0.55)"
          strokeWidth="1.2"
        />
        {/* contents */}
        <g clipPath="url(#tillJarClip)">
          {frac > 0 && (
            <rect
              x="5" y={fillY} width="14" height={fillH + 1}
              fill={`url(#tillFill-${glyph === '⚡' ? 'spark' : 'coin'})`}
              opacity="0.9"
            />
          )}
          {/* dark ember when broke — warm, alive, "needs love" */}
          {lv === 0 && (
            <circle className="till-jar-ember" cx="12" cy="23" r="2.4" fill="#8a5a2b" />
          )}
        </g>
        {/* lid */}
        <rect x="7" y="5" width="10" height="3" rx="1.2" fill="rgba(255,255,255,0.5)" />
        <rect x="9" y="3.2" width="6" height="2.2" rx="1" fill="rgba(255,255,255,0.35)" />
        {/* glass shine */}
        <path d="M8 10 q-1 6 1 12" stroke="rgba(255,255,255,0.35)" strokeWidth="0.9" fill="none" strokeLinecap="round" />
      </svg>
      {/* floating motes — center card only, level-many */}
      {Array.from({ length: motes }).map((_, i) => (
        <span
          key={i}
          className="till-jar-particle"
          style={{
            position: 'absolute',
            left: `${30 + i * 20}%`,
            bottom: '28%',
            width: 3, height: 3, borderRadius: '50%',
            background: skin.particle,
            animationDelay: `${i * 0.8}s`,
            pointerEvents: 'none',
          }}
        />
      ))}
    </span>
  );
}
